import { DESCRIPTION_MAX_LENGTH, TITLE_MAX_LENGTH } from './validation';

import type { ObjectiveDraft, ObjectiveDraftKeyResult } from '@/types/objectives';

import { KeyResultUnit } from '@/constants/KeyResultUnit';

const KEY_RESULT_UNITS = new Set<string>(Object.values(KeyResultUnit));

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const nonEmptyText = (value: unknown): string | null =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

/**
 * One proposed key result, or null when it would not survive the form's own rules (`validation.ts`):
 * a title, a known unit (an unknown one falls back to a plain number), a target different from the
 * start (below it for a measure to reduce) except for yes/no, which is always 0 to 1. A draft must
 * never leave the form invalid.
 */
const parseKeyResult = (value: unknown): ObjectiveDraftKeyResult | null => {
    if (!isRecord(value)) {
        return null;
    }

    const title = nonEmptyText(value.title);
    if (!title) {
        return null;
    }

    const unit =
        typeof value.unit === 'string' && KEY_RESULT_UNITS.has(value.unit)
            ? (value.unit as ObjectiveDraftKeyResult['unit'])
            : KeyResultUnit.NUMBER;

    if (unit === KeyResultUnit.YES_NO) {
        return { title: title.slice(0, TITLE_MAX_LENGTH), unit, startValue: 0, targetValue: 1 };
    }

    if (
        !isFiniteNumber(value.startValue) ||
        !isFiniteNumber(value.targetValue) ||
        value.targetValue === value.startValue
    ) {
        return null;
    }

    return {
        title: title.slice(0, TITLE_MAX_LENGTH),
        unit,
        startValue: value.startValue,
        targetValue: value.targetValue,
    };
};

/**
 * Reads the draft the AI hub delivers (the backend already stripped any preamble and checked that
 * `title` and `description` are non-empty strings). Returns null when the content is still not a
 * usable draft, so the caller reports a failure instead of applying garbage. Texts are clipped to
 * the form's limits: the model is asked for 90 characters, the form refuses more than 150.
 */
export const parseObjectiveDraft = (content: string): ObjectiveDraft | null => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(content);
    } catch {
        return null;
    }

    if (!isRecord(parsed)) {
        return null;
    }

    const title = nonEmptyText(parsed.title);
    const description = nonEmptyText(parsed.description);
    if (!title || !description) {
        return null;
    }

    const keyResults = Array.isArray(parsed.keyResults)
        ? parsed.keyResults
              .map(parseKeyResult)
              .filter((keyResult): keyResult is ObjectiveDraftKeyResult => keyResult !== null)
        : [];

    return {
        title: title.slice(0, TITLE_MAX_LENGTH),
        description: description.slice(0, DESCRIPTION_MAX_LENGTH),
        keyResults,
    };
};

const escapeHtml = (text: string): string =>
    text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

/**
 * The description editor stores HTML; the model writes plain text (and is told to). One paragraph
 * per non-empty line, every character escaped: markup the model might slip in is shown, never
 * interpreted.
 */
export const draftDescriptionToHtml = (text: string): string =>
    text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0)
        .map((line) => `<p>${escapeHtml(line)}</p>`)
        .join('');
